import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import Chart from 'chart.js/auto';
import { AuthService } from '../../services/auth.service';
import { Observable } from 'rxjs';
import { Student } from '../../interfaces/student.interfaces';

// Utility type to flatten nested objects
type FlattenObject<T> = {
  [K in keyof T]: T[K] extends object ? FlattenObject<T[K]> : T[K];
};

// Create a type with all possible paths in the Student interface
type StudentPaths = {
  [K in keyof FlattenObject<Student>]: K;
}[keyof FlattenObject<Student>];

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  pendingActivities: Student['assignedActivities'] = [];
  activityHistory: Student['assignedActivities'] = [];
  progressPercentage = 0;
  private progressChart: Chart | null = null;
  private activitiesChart: Chart | null = null;
  expandedComments = new Set<string>();

  userInfo$: Observable<Student>;
  currentUser: any;
  student: Student | null = null;
  isLoading = true;
  displayFields: { key: StudentPaths; label: string }[] = [];

  constructor() {
    this.userInfo$ = this.authService.getUserInfo();
  }

  ngOnInit() {
    this.currentUser = this.authService.getCurrentUser();
    if (this.currentUser?.role === 'volunteer') {
      this.router.navigate(['/volunteer-home']);
      return;
    }

    this.userInfo$.subscribe((userInfo) => {
      if (!userInfo) {
        return;
      }

      this.isLoading = false;

      // Normalizar horas con reglas de negocio claras para que ningún contador quede en blanco
      const horasCubrir = Number(userInfo.horasCubrir) > 0 ? Number(userInfo.horasCubrir) : 480;

      // Calcular horas acreditadas si no están presentes o si accreditations contiene horas aprobadas
      const accreditedFromAccreditations = (userInfo.accreditations ?? [])
        .filter((a) => a.status === 'approved')
        .reduce((sum, a) => sum + (Number(a.hoursAccredited) || Number(a.hoursPerformed) || 0), 0);

      const horasAcreditadas = Math.max(Number(userInfo.horasAcreditadas) || 0, accreditedFromAccreditations);

      const horasPendientes =
        userInfo.horasPendientes !== undefined && userInfo.horasPendientes !== null
          ? Number(userInfo.horasPendientes)
          : Math.max(0, horasCubrir - horasAcreditadas);

      const progress =
        horasCubrir > 0
          ? Math.min(100, Math.round((horasAcreditadas / horasCubrir) * 100))
          : 0;

      // Asignar en userInfo normalizado
      userInfo.horasCubrir = horasCubrir;
      userInfo.horasAcreditadas = horasAcreditadas;
      userInfo.horasPendientes = horasPendientes;
      this.progressPercentage = progress;
      this.student = userInfo;

      // ACTIVIDADES PENDIENTES
      this.pendingActivities = (userInfo.assignedActivities ?? []).filter(
        (activity) =>
          activity.status === 'registered' ||
          activity.status === 'attended'
      );

      // HISTORIAL
      this.activityHistory = (userInfo.assignedActivities ?? []).filter(
        (activity) =>
          activity.status === 'validated' ||
          activity.status === 'absent'
      );

      this.cdr.detectChanges();
      this.scheduleChartRender(userInfo);
    });

    this.currentUser = this.authService.getCurrentUser();
    this.generateDisplayFields();
  }

  ngOnDestroy(): void {
    if (this.progressChart) {
      this.progressChart.destroy();
      this.progressChart = null;
    }
    if (this.activitiesChart) {
      this.activitiesChart.destroy();
      this.activitiesChart = null;
    }
  }

  generateDisplayFields(): void {
    this.displayFields = [
      { key: 'id', label: 'ID' },
      { key: 'authId', label: 'Auth ID' },
      { key: 'auditorName', label: 'Auditor Name' },
      { key: 'birthDate', label: 'Birth Date' },
      { key: 'career', label: 'Career' },
      { key: 'fullName', label: 'Full Name' },
      { key: 'mail', label: 'Mail' },
      { key: 'phone', label: 'Phone' },
      { key: 'school', label: 'School' },
      { key: 'schoolMail', label: 'School Mail' },
      { key: 'semester', label: 'Semester' },
    ];
  }

  getNestedValue(obj: any, path: string | undefined): any {
    if (!path) return undefined;
    return path
      .split('.')
      .reduce(
        (o, key) => (o && o[key] !== undefined ? o[key] : undefined),
        obj,
      );
  }

  private scheduleChartRender(student: Student, retries = 5): void {
    setTimeout(() => {
      const pieCanvas = document.getElementById('myChart') as HTMLCanvasElement | null;
      const lineCanvas = document.getElementById('lineChart') as HTMLCanvasElement | null;
      if (!pieCanvas || !lineCanvas) {
        if (retries > 0) {
          this.scheduleChartRender(student, retries - 1);
        }
        return;
      }
      this.createChart(student, pieCanvas);
      this.createLineChart(student, lineCanvas);
    }, 60);
  }

  createChart(student: Student, ctx?: HTMLCanvasElement | null): void {
    const canvas = ctx || (document.getElementById('myChart') as HTMLCanvasElement | null);
    if (!canvas) {
      return;
    }

    const existing = Chart.getChart(canvas);
    if (existing) {
      existing.destroy();
    }
    if (this.progressChart) {
      this.progressChart.destroy();
      this.progressChart = null;
    }

    const completedHours = Number(student.horasAcreditadas) || 0;
    const remainingHours =
      Number(student.horasPendientes) ||
      Math.max(0, (Number(student.horasCubrir) || 480) - completedHours);

    // Si ambos son 0, mostrar distribución 0 vs 480 para evitar gráfico vacío
    const dataValues =
      completedHours === 0 && remainingHours === 0
        ? [0, 480]
        : [completedHours, remainingHours];

    this.progressChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: ['Horas acreditadas', 'Horas pendientes'],
        datasets: [
          {
            data: dataValues,
            backgroundColor: ['#24366f', '#f97316'], // Azul ABAQ y Naranja ABAQ
            hoverBackgroundColor: ['#1a2853', '#ea580c'],
            borderColor: ['#ffffff', '#ffffff'],
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 14,
              padding: 16,
              font: { weight: 600, size: 12 },
              color: '#334155',
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => ` ${context.label}: ${context.raw} hrs`,
            },
          },
        },
        cutout: '62%',
      },
    });
  }

  createLineChart(student: Student, ctx?: HTMLCanvasElement | null): void {
    const canvas = ctx || (document.getElementById('lineChart') as HTMLCanvasElement | null);
    if (!canvas) {
      return;
    }

    const existing = Chart.getChart(canvas);
    if (existing) {
      existing.destroy();
    }
    if (this.activitiesChart) {
      this.activitiesChart.destroy();
      this.activitiesChart = null;
    }

    // Recolectar puntos cronológicos de acreditaciones y actividades validadas
    interface TimelinePoint {
      date: Date;
      hours: number;
      label: string;
    }

    const points: TimelinePoint[] = [];

    // Fecha de arranque del servicio social
    const startDate = student.fechaArranque
      ? new Date(student.fechaArranque)
      : new Date(Date.now() - 60 * 24 * 3600 * 1000);

    points.push({
      date: startDate,
      hours: 0,
      label: 'Inicio',
    });

    // Acreditaciones aprobadas
    (student.accreditations ?? [])
      .filter((a) => a.status === 'approved' && a.date)
      .forEach((a) => {
        const hrs = Number(a.hoursAccredited) || Number(a.hoursPerformed) || 0;
        if (hrs > 0) {
          points.push({
            date: new Date(a.date!),
            hours: hrs,
            label: a.comments || a.type || 'Acreditación',
          });
        }
      });

    // Actividades asignadas validadas
    (student.assignedActivities ?? [])
      .filter((act) => act.status === 'validated' && (act.dueDate || act.assignedAt))
      .forEach((act) => {
        const hrs = Number(act.validatedHours) || Number(act.valueInHours) || 0;
        if (hrs > 0) {
          points.push({
            date: new Date(act.dueDate || act.assignedAt!),
            hours: hrs,
            label: act.title || 'Actividad',
          });
        }
      });

    // Ordenar cronológicamente
    points.sort((a, b) => a.date.getTime() - b.date.getTime());

    const labels: string[] = [];
    const cumulativeHours: number[] = [];
    let runningTotal = 0;

    if (points.length <= 1) {
      const currentHrs = student.horasAcreditadas ?? 0;
      labels.push(this.formatDateLabel(startDate));
      cumulativeHours.push(0);

      labels.push('Avance actual');
      cumulativeHours.push(currentHrs);

      labels.push('Meta (480h)');
      cumulativeHours.push(student.horasCubrir || 480);
    } else {
      points.forEach((pt, index) => {
        runningTotal += pt.hours;
        labels.push(index === 0 ? 'Inicio' : this.formatDateLabel(pt.date));
        cumulativeHours.push(runningTotal);
      });

      // Añadir hito de hoy si la última fecha tiene más de 7 días
      const lastPointDate = points[points.length - 1].date;
      const today = new Date();
      if (today.getTime() - lastPointDate.getTime() > 24 * 3600 * 1000 * 7) {
        labels.push('Hoy');
        cumulativeHours.push(runningTotal);
      }
    }

    this.activitiesChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Horas acumuladas',
            data: cumulativeHours,
            borderColor: '#ea580c', // Naranja vibrante
            backgroundColor: 'rgba(36, 54, 111, 0.12)', // Azul suave institucional translúcido
            borderWidth: 3,
            pointBackgroundColor: '#ea580c',
            pointBorderColor: '#24366f', // Azul borde
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointHoverBackgroundColor: '#24366f',
            pointHoverBorderColor: '#ea580c',
            fill: true,
            tension: 0.35,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Horas acumuladas',
              color: '#64748b',
              font: { weight: 600, size: 11 },
            },
            grid: {
              color: '#f1f5f9',
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#64748b',
              font: { size: 11 },
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              boxWidth: 14,
              font: { weight: 600, size: 12 },
              color: '#24366f',
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => ` Horas acumuladas: ${context.raw} hrs`,
            },
          },
        },
      },
    });
  }

  private formatDateLabel(date: Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const month = months[d.getMonth()] || '';
    return `${day} ${month}`;
  }

  toggleComment(activityRef: string): void {
    if (this.expandedComments.has(activityRef)) {
      this.expandedComments.delete(activityRef);
    } else {
      this.expandedComments.add(activityRef);
    }
  }
}
